import type { ReactNode } from 'react';
import {
  CatalogueList,
  newestPublished,
  newestVersion,
  type CatalogueVersionRow,
} from '../catalogue/CatalogueList';
import { ghostButtonClass, primaryButtonClass } from '../points/controls';

/**
 * §9.1 — the salary model catalogue.
 *
 * <b>Deliberately the points systems list, with a different noun.</b> §4.1 asks for exactly that:
 * the same status pills, the same shield on a platform-owned row, the same <em>Clone to edit</em>
 * where an Edit control would refuse, the same Archive offered ahead of Delete — because an
 * operator who has learned the points system builder already knows this screen. It is not a
 * lookalike: the table is `CatalogueList`, the same component that screen renders, so the two
 * cannot drift.
 *
 * What differs is what a salary model *is*, and the copy says it: a points system decides what a
 * finish is worth, a salary model decides what a player costs to roster. The usage column counts
 * contests rather than leaderboards.
 */

export type SalaryModelVersionRow = CatalogueVersionRow;

export interface SalaryModelRow {
  id: string;
  name: string;
  description: string | null;
  isPlatform: boolean;
  isArchived: boolean;
  clonedFromSalaryModelId: string | null;
  versions: SalaryModelVersionRow[];
}

export function SalaryModelList({
  models,
  usage,
  usageLabel,
  usageHint,
  includeArchived,
  onIncludeArchivedChange,
  onNew,
  onOpen,
  onClone,
  onArchive,
  onUnarchive,
  onDelete,
  canManage,
  canCreate = true,
  nameOf,
  notice,
}: {
  models: readonly SalaryModelRow[];
  /**
   * How many contests use each model, or null where the client hasn't been given the contests to
   * count. Null renders as "—", never as 0 — see `CatalogueList`.
   */
  usage: ReadonlyMap<string, number> | null;
  usageLabel: string;
  usageHint?: string;
  includeArchived: boolean;
  onIncludeArchivedChange: (next: boolean) => void;
  /** `reference` starts from §4.2's published model; `blank` from the smallest usable one. */
  onNew: (start: 'blank' | 'reference') => void;
  onOpen: (model: SalaryModelRow) => void;
  onClone?: (model: SalaryModelRow) => void;
  onArchive: (model: SalaryModelRow) => void;
  onUnarchive: (model: SalaryModelRow) => void;
  onDelete: (model: SalaryModelRow) => void;
  /** False for a platform model seen by a room: visible, usable, never editable. */
  canManage: (model: SalaryModelRow) => boolean;
  canCreate?: boolean;
  nameOf?: (id: string) => string | null;
  notice?: ReactNode;
}) {
  return (
    <CatalogueList
      items={models}
      facts={(model) => ({
        id: model.id,
        name: model.name,
        description: model.description,
        isPlatform: model.isPlatform,
        isArchived: model.isArchived,
        clonedFromId: model.clonedFromSalaryModelId,
        versions: model.versions,
      })}
      copy={{
        title: 'Salary models',
        rowLabel: 'Model',
        blurb:
          'What a player costs to put on a fantasy roster. A model is built and tried out here on its own; a contest picks one up when it is published, and keeps that exact version for good.',
        newLabel: 'New model',
        usageLabel,
        usageHint,
        emptyVersions: 'Nothing priced yet',
      }}
      usage={usage}
      includeArchived={includeArchived}
      onIncludeArchivedChange={onIncludeArchivedChange}
      onNew={() => onNew('blank')}
      onOpen={onOpen}
      onClone={onClone}
      onArchive={onArchive}
      onUnarchive={onUnarchive}
      onDelete={onDelete}
      canManage={canManage}
      canCreate={canCreate}
      nameOf={nameOf}
      notice={notice}
      emptyState={
        <EmptyState onNew={onNew} canCreate={canCreate} includeArchived={includeArchived} />
      }
    />
  );
}

/**
 * Start from something rather than a blank form — §9.7's habit, for the same reason.
 *
 * Nobody meeting this screen knows what a sensible price ladder looks like. The reference model is
 * a real one whose weighting was derived from measured data (§4.3), so it is a better first draft
 * than an empty table, and every figure in it is editable the moment it lands.
 */
function EmptyState({
  onNew,
  canCreate,
  includeArchived,
}: {
  onNew: (start: 'blank' | 'reference') => void;
  canCreate: boolean;
  includeArchived: boolean;
}) {
  if (!canCreate) {
    return (
      <p className="px-5 py-10 text-center text-sm text-ink-800/55">
        No salary models are available to this room yet.
      </p>
    );
  }

  return (
    <div className="px-5 py-10 text-center">
      <p className="text-sm font-medium text-ink-950">No salary models yet</p>
      <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-ink-800/60">
        A salary model works out what each player should cost, from how they did in past series, so
        entrants pick a team under a budget instead of everybody picking the same eight names.
      </p>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        <button type="button" onClick={() => onNew('reference')} className={primaryButtonClass}>
          Start from the reference model
        </button>
        <button type="button" onClick={() => onNew('blank')} className={ghostButtonClass}>
          Start from scratch
        </button>
      </div>
      {!includeArchived ? (
        <p className="mt-3 text-[11px] text-ink-800/40">
          Archived models are hidden — tick “Show archived” if you are looking for one.
        </p>
      ) : null}
    </div>
  );
}

/** The version a reader means by "the current one": the highest number, whatever its status. */
export function currentSalaryVersion(model: SalaryModelRow): SalaryModelVersionRow | null {
  return newestVersion(model.versions);
}

/** The newest published version — what a contest publishing today would pin. */
export function latestPublishedSalaryVersion(model: SalaryModelRow): SalaryModelVersionRow | null {
  return newestPublished(model.versions);
}
